import React, { memo, useCallback, useMemo, useState } from 'react';
import Carousel from 'react-grid-carousel';

import { Box, Button, Grid, Typography } from '@mui/material';
import { IMAGES } from '@provider-portal/promotions-common/components/SetupNewPromotion/images';

import { PromotionDetails } from '../../types';
import { ActivePromotion } from './ActivePromotionsComponent';
import { UpcomingPromotion } from './UpcomingPromotionsComponent';

export interface ActivePromotionsGridFCProps {
  strings: {
    buttons: {
      showAllActive: string;
    };
    page: {
      title: string;
      subtitle: string;
      subtitleNoOrders: string;
    };
    promotionCard: {
      viewDetails: string;
      tooltipInfo: string;
    };
    upcomingPromotionsCard: {
      upcoming: string;
    };
    emptyActivePromotions: {
      title: string;
      subtitle: string;
    };
  };
  promotionDetails: PromotionDetails[];
  lastUpdate: Date;
  isUpdatedByOrder: boolean;
  promotionClicked: (id: number) => void;
  rowsPerEndedPromotions?: number;
  canSelfAssignCampaigns: boolean;
  shouldShowBannerIfNoActivePromotions: boolean;
  hideShowAllPromotionsButton?: boolean;
}

const styles = {
  carouselContainer: {
    margin: '0 -20px',
  },
  carouselButton: {
    marginTop: '16px',
  },
  emptyActivePromotions: {
    title: {
      fontStyle: 'normal',
      fontWeight: 700,
      fontSize: '24px',
      lineHeight: '32px',
    },
    subtitle: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: '24px',
      color: '#585F68',
      marginTop: '8px',
    },
  },
} as const;

const MAX_COLUMNS = 2;

export const ActivePromotionsGridFC: React.FC<ActivePromotionsGridFCProps> = memo(
  ({
    strings,
    promotionDetails,
    promotionClicked,
    shouldShowBannerIfNoActivePromotions,
    hideShowAllPromotionsButton = false,
  }) => {
    const [isShowAllActiveFlag, setIsShowAllActiveFlag] = useState<boolean>(false);

    const onShowAllActiveButtonClick = useCallback(() => {
      setIsShowAllActiveFlag(!isShowAllActiveFlag);
    }, [isShowAllActiveFlag]);

    const gridRows = useMemo(() => {
      return isShowAllActiveFlag ? promotionDetails.length / MAX_COLUMNS : 1;
    }, [isShowAllActiveFlag, promotionDetails]);

    if (!shouldShowBannerIfNoActivePromotions && promotionDetails.length === 0) {
      return null;
    }

    const ongoingPromotions = promotionDetails.filter((promotions) => {
      const promotionStartTimestamp = Number(promotions.startDate);
      return promotionStartTimestamp <= Date.now();
    });

    const upcomingPromotions = promotionDetails.filter((promotions) => {
      const promotionStartTimestamp = Number(promotions.startDate);
      return promotionStartTimestamp > Date.now();
    });

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginLeft: '0%',
          flex: 1,
        }}
      >
        <Box
          alignSelf="left"
          justifyContent="space-between"
          mb={2}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'left',
            alignItems: 'flex-left',
          }}
        >
          <Typography variant="h5" alignSelf="center">
            {strings.page.title}
          </Typography>
        </Box>

        <Box>
          {promotionDetails.length === 0 && (
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <img
                  src={IMAGES.emptyActivePromotions.url}
                  alt={strings.emptyActivePromotions.title}
                  width={IMAGES.emptyActivePromotions.width}
                />
              </Grid>
              <Grid item>
                <Typography sx={styles.emptyActivePromotions.title}>
                  {strings.emptyActivePromotions.title}
                </Typography>
                <Typography sx={styles.emptyActivePromotions.subtitle}>
                  {strings.emptyActivePromotions.subtitle}
                </Typography>
              </Grid>
            </Grid>
          )}

          {promotionDetails.length > 0 && (
            <Carousel
              rows={gridRows}
              cols={MAX_COLUMNS}
              gap={24}
              containerStyle={styles.carouselContainer}
            >
              {ongoingPromotions.map((obj) => {
                return (
                  <Carousel.Item key={obj.id}>
                    <ActivePromotion
                      promotionDetail={obj}
                      strings={{ ...strings }}
                      promotionClicked={promotionClicked}
                    />
                  </Carousel.Item>
                );
              })}

              {upcomingPromotions.map((obj) => {
                return (
                  <Carousel.Item key={obj.id}>
                    <UpcomingPromotion
                      promotionDetail={obj}
                      strings={{ ...strings }}
                      promotionClicked={promotionClicked}
                    />
                  </Carousel.Item>
                );
              })}
            </Carousel>
          )}
          {!hideShowAllPromotionsButton && !isShowAllActiveFlag && promotionDetails.length > 2 && (
            <Box sx={styles.carouselButton}>
              <Button variant="contained" color="secondary" onClick={onShowAllActiveButtonClick}>
                {strings.buttons.showAllActive}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    );
  },
);
